import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import { IsencaoIRResultType, ResultMessages } from "../calculadora-isencao-ir";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

export const CalculadoraIsencaoResponse = ({
  results,
}: {
  results: IsencaoIRResultType;
}) => {
  const [showDetalhes, setShowDetalhes] = useState(false);

  const renderStatusCard = () => {
    const statusConfig = {
      JA_ISENTO: {
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        textColor: "text-blue-800",
        icon: <Info className="h-5 w-5 text-blue-600" />,
        message: ResultMessages.JA_ISENTO,
      },
      BENEFICIADO_TOTAL: {
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-800",
        icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
        message: ResultMessages.BENEFICIADO_TOTAL,
      },
      BENEFICIADO_PARCIAL: {
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        textColor: "text-green-800",
        icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
        message: ResultMessages.BENEFICIADO_PARCIAL,
      },
      NAO_BENEFICIADO: {
        bgColor: "bg-gray-50",
        borderColor: "border-gray-200",
        textColor: "text-gray-800",
        icon: <AlertCircle className="h-5 w-5 text-gray-600" />,
        message: ResultMessages.NAO_BENEFICIADO,
      },
    };

    const config = statusConfig[results.faixaBeneficio];

    return (
      <div className={`p-4 ${config.bgColor} rounded border ${config.borderColor} mb-6`}>
        <div className="flex items-start gap-3">
          {config.icon}
          <div>
            <h3 className={`font-semibold ${config.textColor} mb-1`}>
              {config.message.TITULO}
            </h3>
            <p className={`text-sm ${config.textColor}`}>
              {config.message.DESCRICAO}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado da Simulação</MainCardTitle>

      {renderStatusCard()}

      {results.economiaMensal > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
          <div className="p-4 bg-primary rounded border flex flex-col justify-center">
            <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0">
              Economia Mensal
            </h2>
            <p className="sm:text-2xl text-xl text-center font-bold text-white m-0">
              <span className="text-sm mr-1 text-white">R$</span>
              {normalSanatizerBRL(results.economiaMensal)}
            </p>
            {results.percentualEconomia > 0 && results.percentualEconomia < 100 && (
              <p className="text-sm text-center text-white opacity-90 mt-1">
                Redução de {normalSanatizerBRL(results.percentualEconomia)}%
              </p>
            )}
            {results.percentualEconomia >= 100 && results.irProposto === 0 && (
              <p className="text-sm text-center text-white opacity-90 mt-1">
                Isenção total do IR
              </p>
            )}
          </div>

          <div className="p-4 bg-white rounded border-2 border-red-200 flex flex-col justify-center">
            <h2 className="sm:text-base text-sm text-center font-semibold m-0">
              Economia Anual**
            </h2>
            <p className="sm:text-2xl text-xl text-center font-black text-red-800 m-0">
              <span className="text-sm mr-1 text-gray-500">R$</span>
              {normalSanatizerBRL(results.economiaAnual)}
            </p>
            <p className="text-xs text-center text-gray-600 mt-1">
              ** Considera 13 parcelas (incluindo 13º salário)
            </p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={() => setShowDetalhes(!showDetalhes)}
          className="w-full p-3 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 text-left transition-colors duration-200"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-800 text-sm">
              Como foi calculado
            </h3>
            <span className="text-blue-600 text-sm">
              {showDetalhes ? "Ocultar" : "Ver Detalhes"}
            </span>
          </div>
        </button>

        {showDetalhes && (
          <div className="mt-3 p-4 bg-blue-50 rounded border border-blue-200 text-xs">
            <h5 className="font-semibold text-blue-800 mb-3">Passo a passo do cálculo:</h5>
            
            <div className="space-y-2">
              <p className="text-blue-700">
                <strong>1. Salário bruto informado:</strong> R$ {normalSanatizerBRL(results.salarioBruto)}
              </p>
              
              <p className="text-blue-700">
                <strong>2. INSS descontado:</strong> R$ {normalSanatizerBRL(results.inssDescontado)}
              </p>
              
              <p className="text-blue-700">
                <strong>3. Base de cálculo do IR:</strong> R$ {normalSanatizerBRL(results.baseCalculoIR)}
                <span className="text-xs block ml-4 mt-1">
                  (Salário bruto - INSS)
                </span>
              </p>
              
              <div className="border-t border-blue-200 pt-2 mt-2">
                <p className="text-blue-700">
                  <strong>4. IR com regra atual (2025):</strong> R$ {normalSanatizerBRL(results.irAtual)}
                  {results.aliquotaAtual > 0 && (
                    <span className="text-xs block ml-4 mt-1">
                      Alíquota efetiva: {normalSanatizerBRL(results.aliquotaAtual)}%
                    </span>
                  )}
                </p>
                
                <p className="text-blue-700 mt-2">
                  <strong>5. IR com nova regra (2026):</strong> R$ {normalSanatizerBRL(results.irProposto)}
                  {results.aliquotaProposta > 0 && (
                    <span className="text-xs block ml-4 mt-1">
                      Alíquota efetiva: {normalSanatizerBRL(results.aliquotaProposta)}%
                    </span>
                  )}
                  {results.irProposto === 0 && results.reducaoAplicada > 0 && (
                    <span className="text-xs block ml-4 mt-1 text-green-700 font-semibold">
                      ✓ Isento pela nova regra (redução aplicada)
                    </span>
                  )}
                </p>
                
                {results.reducaoAplicada > 0 && (
                  <p className="text-blue-700 mt-2">
                    <strong>6. Redução aplicada (PL 1.087/2025):</strong> R$ {normalSanatizerBRL(results.reducaoAplicada)}
                  </p>
                )}
              </div>
              
              {results.economiaMensal > 0 && (
                <div className="border-t border-blue-200 pt-2 mt-2">
                  <p className="text-blue-700 font-bold text-base">
                    = Economia mensal: R$ {normalSanatizerBRL(results.economiaMensal)}
                  </p>
                  <p className="text-blue-700 font-bold">
                    = Economia anual (13 parcelas): R$ {normalSanatizerBRL(results.economiaAnual)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h4 className="font-bold text-primary text-base mb-3 text-center">
          Comparação: Regra Atual vs Nova Regra
        </h4>
        
        <div className="overflow-x-auto">
          <table className="bg-white rounded w-full border">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-4 py-2 text-center text-sm font-semibold">Período</th>
                <th className="px-4 py-2 text-center text-sm font-semibold">IR Atual (2025)</th>
                <th className="px-4 py-2 text-center text-sm font-semibold">IR Proposto (2026)</th>
                <th className="px-4 py-2 text-center text-sm font-semibold">Economia</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="px-4 py-2 text-center font-medium">Mensal</td>
                <td className="px-4 py-2 text-center">
                  R$ {normalSanatizerBRL(results.irAtual)}
                </td>
                <td className="px-4 py-2 text-center">
                  R$ {normalSanatizerBRL(results.irProposto)}
                </td>
                <td className="px-4 py-2 text-center font-bold text-green-700">
                  R$ {normalSanatizerBRL(results.economiaMensal)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-center font-medium">Anual*</td>
                <td className="px-4 py-2 text-center">
                  R$ {normalSanatizerBRL(results.irAtual * 13)}
                </td>
                <td className="px-4 py-2 text-center">
                  R$ {normalSanatizerBRL(results.irProposto * 13)}
                </td>
                <td className="px-4 py-2 text-center font-bold text-green-700">
                  R$ {normalSanatizerBRL(results.economiaAnual)}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-gray-600 mt-2 text-center">
            * Considera 13 parcelas (incluindo 13º salário)
          </p>
        </div>
      </div>

      <div className="p-3 bg-gray-50 rounded border border-gray-200 text-xs mb-4">
        <h5 className="font-semibold text-gray-800 mb-2">Como funciona a redução do PL 1.087/2025:</h5>
        <ul className="space-y-1 text-gray-700">
          <li>• <strong>Base até R$ 5.000:</strong> Redução de até R$ 312,89</li>
          <li>• <strong>Base de R$ 5.000,01 a R$ 7.350:</strong> Redução decresce progressivamente até zerar</li>
          <li>• <strong>Base acima de R$ 7.350:</strong> Sem redução</li>
          <li>• A redução é sempre limitada ao valor do IR calculado</li>
          <li>• Esta calculadora não considera a sobretaxa para rendimentos acima de R$ 50.000/mês</li>
        </ul>
      </div>
    </MainCard>
  );
};